import { DotsThree, type Icon as PhosphorIcon } from '@phosphor-icons/react';
import { Icon } from '../Icon';
import { Popover } from '../ui/popover';

export interface RowMenuItem {
  label: string;
  onSelect: () => void;
  /** The icon before the label (Figma A04.1 row menu). */
  icon?: PhosphorIcon;
  /** A quiet note at the right edge ("Lead", "6 open"). */
  hint?: string;
  /** A hairline above this item, setting it apart from the ones before. */
  separated?: boolean;
}

/**
 * The "⋯" menu at the end of a table row (Figma A04.1 "Row menu"). Closes itself after a choice; Esc and an outside
 * click close it too. It is drawn over the page, so the table it sits in never clips it.
 */
export function RowMenu({
  label,
  items,
  round = false,
}: {
  label: string;
  items: RowMenuItem[];
  /** The 38px outlined circle of a page header (Figma A04.2) instead of the bare dots of a table row. */
  round?: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <Popover
      role="menu"
      className="w-(--mbm-layout-row-menu-width) py-(--mbm-space-1-5)"
      trigger={(props) => (
        <button
          type="button"
          aria-label={label}
          {...props}
          className={
            round
              ? 'flex size-(--mbm-size-toolbar-control-lg) items-center justify-center rounded-full border border-border-input bg-surface-card text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)'
              : 'flex size-(--mbm-space-7) items-center justify-center rounded-full text-text-primary hover:bg-surface-field focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)'
          }
        >
          <Icon icon={DotsThree} size="md" />
        </button>
      )}
    >
      {(close) => (
        <ul className="flex flex-col">
          {items.map((item) => (
            <li key={item.label} className={item.separated ? 'mt-px border-t border-border-line' : undefined}>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  item.onSelect();
                }}
                className="flex h-(--mbm-size-menu-item) w-full items-center gap-(--mbm-space-2-5) px-(--mbm-space-3-5) text-left text-label-medium text-text-primary hover:bg-surface-tint focus-visible:bg-surface-tint focus-visible:outline-none"
              >
                {item.icon ? <Icon icon={item.icon} size="sm" /> : null}
                <span className="flex-1 truncate">{item.label}</span>
                {item.hint ? <span className="text-caption font-normal text-text-tertiary">{item.hint}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Popover>
  );
}
